import { createSelector } from '@reduxjs/toolkit'
import { selectContext } from './contextSlice'
import { selectHeader } from './features/headerSlice'
import { selectSidebar } from './features/sidebarSlice'
import { selectTheme, selectThemeSidebar } from './themeSlice'

import type { ContextInitState } from './features/types'
import type { RootState } from '.'
import { selectResUi } from './features/resUiSlice'
import { parseMoneyString } from './features/utils'

/* fn / state selectors */
export const selectPersonFullName = (globalState: ContextInitState) => globalState.personFullName
export const selectBusinessSwapperOptions = (globalState: ContextInitState) => globalState.memberAreaBusinessSwapperOptions
export const selectRentalTeamSwapperOptions = (globalState: ContextInitState) => globalState.rentalTeamSwapperOptions
export const selectUserContext = (globalState: ContextInitState) => globalState.userContext
export const selectPersonId = (globalState: ContextInitState) => globalState.personId

export const selectResApproval = (state: RootState) => state.resApproval
export const selectContextDropDowns = (state: RootState) => state.contextDropDowns
export const selectResVehicle = (state: RootState) => state.vehicle
export const selectResEditForm = (state: RootState) => state.resEditForm

/* logic selectors */
export const selectAccount = createSelector(selectContextDropDowns, dropDowns => dropDowns.account)
export const selectBooker = createSelector(selectContextDropDowns, dropDowns => dropDowns.booker)
export const selectDriver = createSelector(selectContextDropDowns, dropDowns => dropDowns.driver)
export const selectCarClubDriver = createSelector(selectContextDropDowns, dropDowns => dropDowns.carClubDriver)
export const selectPermissions = createSelector(selectContext, values => values.permissions)

export const selectApplicableTarrifBandArgs = createSelector(
	[selectResEditForm, selectResVehicle, selectAccount, selectDriver],
	(resForm, vehicle, account, driver) => {
		const { reasonForHire, startDate, endDate, totalDays } = resForm
		const { vehicleDetails } = vehicle
		const { businessId } = account
		const { driverId } = driver
		return {
			reasonForHire,
			startDate,
			vehicleGroupId: vehicleDetails.vehicleGroupId || vehicleDetails.id,
			businessId,
			driverId,
			endDate,
			totalDays,
		}
	}
)

export const selectSidebarContext = createSelector(
	[selectPersonFullName, selectBusinessSwapperOptions, selectRentalTeamSwapperOptions],
	(personFullName, memberAreaBusinessSwapperOptions, rentalTeamSwapperOptions) => ({
		personFullName,
		memberAreaBusinessSwapperOptions,
		rentalTeamSwapperOptions,
	})
)

export const selectHeaderContext = createSelector(
	[selectPersonId, selectUserContext, selectPersonFullName],
	(personId, userContext, personFullName) => ({
		userHref: `/person/${personId}/`,
		userContext,
		personFullName,
	})
)

/* derived selectors */
/* res total */
export const selectResTotal = createSelector([selectContext, selectResUi], (context, resUi) => {
	const { total, chargeableItems } = resUi
	const { countryCode, currency } = context
	const itemsTotals = chargeableItems.reduce((total, item) => {
		if (item) {
			total += Number(item.rates.net)
		}
		return total
	}, 0)
	const t = parseMoneyString(total)
	let amount = '0'
	try {
		amount = `${new Intl.NumberFormat(countryCode, { style: 'currency', currency, maximumFractionDigits: 2 }).format(t + itemsTotals)}`
	} catch (e) {
		amount = '0'
		// throw new Error('error formatting final price')
	}
	return amount
})

/* res rate */
export const selectResRate = createSelector([selectContext, selectResUi], (context, resUi) => {
	const { rate } = resUi
	const { countryCode, currency } = context

	if (rate == '0.00') return 'POA'

	const r = parseMoneyString(rate)
	let formattedRate = '0'
	try {
		formattedRate = `${new Intl.NumberFormat(countryCode, { style: 'currency', currency, maximumFractionDigits: 2 }).format(Number(r))}`
	} catch (e) {
		formattedRate = '0'
	}
	return formattedRate
})

/* sidebar */
export const selectFODSidebarProps = createSelector(
	[selectSidebar, selectContext, selectThemeSidebar],
	(navLinks, sidebarContext, sidebar) => ({
		...navLinks,
		...selectSidebarContext(sidebarContext),
		sidebar,
	})
)

/* header */
export const selectFODHeaderProps = createSelector([selectHeader, selectContext, selectTheme], (searchOptions, context, theme) => ({
	...searchOptions,
	...selectHeaderContext(context),
	theme,
}))
export const selectBespokeFieldQueries = createSelector([selectDriver, selectAccount, selectResEditForm], (driver, account, resForm) => {
	const { businessId } = account
	const { driverId } = driver
	const { reasonForHire, tariffBandId } = resForm
	return { businessId, driverId, reasonForHire, tariffBandId }
})

export const selectApprovalProps = createSelector(
	[selectResVehicle, selectResEditForm, selectResApproval, selectContextDropDowns],
	(resVehicle, resEditForm, resApproval, contextDropDowns) => {
		const { driverId } = contextDropDowns.driver
		const { businessId } = contextDropDowns.account
		const { numDays } = resApproval
		const { insuranceType, tariffBandId } = resEditForm
		const { vehicleGroupId } = resVehicle
		return {
			numDays,
			driverId,
			businessId,
			insuranceType,
			tariffBandId,
			vehicleGroupId,
		}
	}
)
